import { StatusCode } from "api/protocols";
import { GetPurchaseOrder } from "api/rpc/2024-04/facilityAdmin/order/purchaseOrder";
import axios, { CancelToken } from "axios";
import { Badge } from "components/badge/Badge";
import DataTable from "components/dataTable/DataTable";
import DropFilter from "components/dropdown/DropFilter";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import Page from "components/page/Page";
import Search from "components/search/Search";
import { errorMessage, valueToString } from "helpers/Helpers";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { showError } from "redux/actions/ui";
import { IPurchaseOrder } from "redux/reducers/models/purchaseOrder";

const TABLE_LIMIT = 50;
const PO_STATUS_FILTER_KEY = "po_status_filters";

type PurchaseOrderFilters = {
    status: Array<string>;
};

export default function PurchaseOrders() {
    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const { filterHistoryStore } = useAppSelector(store => store);
    const [purchaseOrders, setPurchaseOrders] = useState<Array<IPurchaseOrder>>([]);
    const [offset, setOffset] = useState(0);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [filters, setFilters] = useState<PurchaseOrderFilters>({
        status: filterHistoryStore[PO_STATUS_FILTER_KEY]
          ? filterHistoryStore[PO_STATUS_FILTER_KEY].currentFilter
          : [],
      });

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getPurcahseOrders(source.token);
        return () => source.cancel();
    }, [offset, searchQuery, filters]);

    async function getPurcahseOrders(cancelToken?: CancelToken) {
        if (purchaseOrders !== undefined) {
            setPurchaseOrders(undefined);
        }
        const res = await GetPurchaseOrder({ offset: offset, limit: TABLE_LIMIT, search: searchQuery, statuses: filters?.status }, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            setPurchaseOrders([]);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(errorMessage(res, t("Error getting purchase orders"))));
            return;
        }
        setPurchaseOrders(res?.data);
    }

    function handleExtendedFilter(filterReturn: Record<string, any>[], type: keyof PurchaseOrderFilters) {
        setFilters(prev => ({
          ...prev,
          [type]: filterReturn.map(filter => filter.id as string),
        }));
      }

    return (
        <Page
            title={t("Purchase Orders")}
            narrow
            primaryAction={{ content: t("New"), action: () => history.push("/admin/settings/purchase-orders/new") }}
        >
            <DropFilter
                title={t("Status")}
                filterData={[{id: "draft", label: t("Draft")}, {id: "ordered", label: t("Ordered")}, {id: "partially_received", label: t("Partially Received")}, {id: "received", label: t("Received")}, {id: "closed", label: t("Closed")}, {id: "cancelled", label: t("Cancelled")}]}
                filterLabelPropFromData="label"
                filterIdPropFromData="id"
                filterType="Checkbox"
                historyKey={PO_STATUS_FILTER_KEY}
                applyFilters={filterReturn => handleExtendedFilter(filterReturn, "status")}
                defaultCheckboxes={filters?.status}
            />
            <br />
            <br />
            <Search
                historyKey={"purchase-orders-search"}
                searchCallback={search => setSearchQuery(search)}
                placeholder={t("Search...")}
                showDropdown={false}
            />
            <br />
            <DataTable
                columns={[{ label: t("PO Number") }, { label: t("Vendor") }, { label: t("Date") }, { label: t("Status") }]}
                loading={purchaseOrders === undefined}
                footer={{
                    tableLimit: TABLE_LIMIT,
                    tableOffset: offset,
                    disableNextOffset: !purchaseOrders || !(purchaseOrders?.length === TABLE_LIMIT),
                    handleTableOffset: direction => direction === "prev" ? setOffset(prev => prev - TABLE_LIMIT) : setOffset(prev => prev + TABLE_LIMIT),
                }}
            >
                {searchQuery?.length > 0 && purchaseOrders?.length === 0 ?
                    <tr>
                        <td colSpan={4}><MessageDisplay icon="magnifying-glass" title={t("No purchase orders found")} content={t("Adjust your search and try again")} />
                        </td>
                    </tr> : purchaseOrders?.length === 0 ?
                        <tr>
                            <td colSpan={4}><MessageDisplay icon="magnifying-glass" title={t("No purchase orders created")} content={t("Create a new purchase order to populate this list")} />
                            </td>
                        </tr> : purchaseOrders?.map((purchaseOrder, index) => {
                            return (
                                <tr key={index} className="clickable" onClick={() => history.push(`/admin/settings/purchase-orders/${purchaseOrder?.id}`)}>
                                    <td>{purchaseOrder?.po_number}</td>
                                    <td>{purchaseOrder?.vendor_name}</td>
                                    <td>{purchaseOrder?.created_at ? moment.utc(purchaseOrder.created_at).local().format("LL") : ""}</td>
                                    <td><Badge size="small" type={purchaseOrder?.status === "closed" ? "success" : purchaseOrder?.status === "cancelled" ? "error" : "gray"}>{valueToString(purchaseOrder?.status)}</Badge></td>
                                </tr>
                            );
                        })}
            </DataTable>

        </Page>
    );
}